// themes.js
import { createTheme, alpha } from '@mui/material/styles';
import typography from './typography';
import shadows from './shadows';
import shape from './shape';

// Brand colour is shared by both modes so the product stays recognisable.
const BRAND = '#04aeea';

/**
 * Everything that differs between light and dark lives here, so the two modes
 * can never drift apart the way two hand-maintained theme objects do.
 */
const modeTokens = {
    light: {
        primary: { main: BRAND, light: '#62deff', dark: '#007fb7', contrastText: '#ffffff' },
        secondary: { main: '#665fe6', light: '#9a8eff', dark: '#3336b3', contrastText: '#ffffff' },
        error: { main: '#f44336', light: '#ff7961', dark: '#ba000d', contrastText: '#ffffff' },
        warning: { main: '#ff9800', light: '#ffc947', dark: '#c66900', contrastText: '#000000' },
        info: { main: '#2196f3', light: '#6ec6ff', dark: '#0069c0', contrastText: '#ffffff' },
        success: { main: '#4caf50', light: '#80e27e', dark: '#087f23', contrastText: '#ffffff' },
        background: { default: '#f7f9fc', paper: '#ffffff' },
        text: { primary: '#334155', secondary: '#64748b', disabled: '#9ca3af' },
        divider: '#e0e7ef',
        // Layered surfaces. Components should reach for these instead of
        // inventing their own greys.
        surface: {
            page: '#f7f9fc',      // app canvas behind cards
            raised: '#ffffff',    // a card sitting on the canvas
            subtle: '#f5f9ff',    // table headers, section headers
            sunken: '#fafcff',    // zebra stripes, wells, read-only fields
            border: '#e0e7ef',
            borderStrong: '#cbd5e1',
        },
        // Accent panels on the dashboard (brand-tinted informational blocks).
        accentPanel: { bg: '#ddeaf6', border: '#547797', contrastBg: '#1e4e79', contrastText: '#ffffff' },
        shadow: {
            card: 'rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px',
            dialog: '0 6px 16px rgba(0, 0, 0, 0.08)',
            tooltip: '0 2px 8px rgba(0, 0, 0, 0.12)',
        },
    },
    dark: {
        primary: { main: BRAND, light: '#64dfff', dark: '#0388b5', contrastText: '#ffffff' },
        secondary: { main: '#a59fff', light: '#d9d4ff', dark: '#665fe6', contrastText: '#ffffff' },
        error: { main: '#f55a4e', light: '#ff867c', dark: '#c62828', contrastText: '#ffffff' },
        warning: { main: '#ffb74d', light: '#ffe082', dark: '#f57c00', contrastText: '#000000' },
        info: { main: '#64b5f6', light: '#90caf9', dark: '#1976d2', contrastText: '#ffffff' },
        success: { main: '#81c784', light: '#a5d6a7', dark: '#388e3c', contrastText: '#ffffff' },
        background: { default: '#0f151d', paper: '#161e28' },
        text: { primary: '#e2e8f0', secondary: '#94a3b8', disabled: '#64748b' },
        divider: '#2a3542',
        surface: {
            page: '#0f151d',
            raised: '#1b2430',
            subtle: '#1e2836',
            sunken: '#141c26',
            border: '#2a3542',
            borderStrong: '#3a4a5e',
        },
        accentPanel: { bg: '#16222f', border: '#33566f', contrastBg: '#13314a', contrastText: '#e6f2fb' },
        shadow: {
            card: 'rgb(0 0 0 / 45%) 0px 0px 2px 0px, rgb(0 0 0 / 35%) 0px 12px 24px -8px',
            dialog: '0 8px 20px rgba(0, 0, 0, 0.5)',
            tooltip: '0 3px 10px rgba(0, 0, 0, 0.5)',
        },
    },
};

const greyScale = {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
};

export const createAppTheme = (mode = 'light') => {
    const t = modeTokens[mode] || modeTokens.light;
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: t.primary,
            secondary: t.secondary,
            error: t.error,
            warning: t.warning,
            info: t.info,
            success: t.success,
            grey: greyScale,
            background: t.background,
            text: t.text,
            divider: t.divider,
            surface: t.surface,
            accentPanel: t.accentPanel,
            appShadow: t.shadow,
        },
        typography: typography,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: t.surface.page,
                        color: t.text.primary,
                        scrollbarColor: `${alpha(t.text.secondary, 0.35)} transparent`,
                    },
                    '*::-webkit-scrollbar': { width: 8, height: 8 },
                    '*::-webkit-scrollbar-track': { background: 'transparent' },
                    '*::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(t.text.secondary, 0.35),
                        borderRadius: 8,
                    },
                    '*::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: alpha(t.text.secondary, 0.55),
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: '8px 16px',
                        boxShadow: 'none',
                    },
                    containedPrimary: {
                        '&:hover': {
                            backgroundColor: t.primary.dark,
                            boxShadow: `0 2px 8px ${alpha(BRAND, isDark ? 0.15 : 0.25)}`,
                        },
                    },
                    outlinedPrimary: {
                        borderColor: BRAND,
                        '&:hover': {
                            backgroundColor: alpha(BRAND, isDark ? 0.12 : 0.04),
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 8,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: BRAND,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: BRAND,
                                borderWidth: 2,
                            },
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        backgroundImage: 'none',
                        backgroundColor: t.surface.raised,
                        boxShadow: t.shadow.dialog,
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        padding: '16px 24px',
                        backgroundColor: alpha(BRAND, isDark ? 0.1 : 0.05),
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: { padding: 24 },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '16px 24px',
                        borderTop: `1px solid ${t.divider}`,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        backgroundImage: 'none',
                        backgroundColor: t.surface.raised,
                        boxShadow: isDark
                            ? '0 3px 10px rgba(0, 0, 0, 0.45)'
                            : '0 2px 8px rgba(0, 0, 0, 0.08)',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: t.surface.raised,
                        color: t.text.primary,
                        boxShadow: t.shadow.tooltip,
                        borderRadius: 8,
                        padding: 12,
                        fontSize: '0.875rem',
                        border: `1px solid ${t.surface.border}`,
                    },
                    arrow: {
                        color: t.surface.raised,
                        '&::before': {
                            border: `1px solid ${t.surface.border}`,
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: 8 },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 8 },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: t.background.paper,
                        '&.Mui-disabled': {
                            backgroundColor: t.surface.sunken,
                        },
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        '&.Mui-expanded': {
                            minHeight: 48,
                            backgroundColor: isDark ? alpha(BRAND, 0.16) : '#caedf9',
                            color: isDark ? t.primary.light : BRAND,
                        },
                        '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                            color: isDark ? t.primary.light : BRAND,
                        },
                    },
                    content: {
                        '&.Mui-expanded': { margin: 0 },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: t.divider },
                    head: { color: t.text.secondary, backgroundColor: t.surface.subtle },
                    body: { color: t.text.primary },
                },
            },
            MuiTabPanel: {
                styleOverrides: {
                    root: { padding: 8 },
                },
            },
        },
        shape: shape,
        shadows: shadows,
        spacing: 8,
    });
};

export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
